import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Checkbox,
  Tabs,
  Tab,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import PeopleIcon from "@material-ui/icons/People";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PersonIcon from "@material-ui/icons/Person";
import InfoIcon from "@material-ui/icons/Info";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2), maxWidth: 720 },
  backButton: { marginRight: theme.spacing(1) },
  paper: { padding: theme.spacing(0, 3, 3), borderRadius: 12, boxShadow: theme.shadows[2] },
  tabs: { borderBottom: `1px solid ${theme.palette.divider}`, marginBottom: theme.spacing(2) },
  tabPanel: { paddingTop: theme.spacing(1) },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    "&::before": {
      content: '""',
      width: 4,
      height: 20,
      backgroundColor: theme.palette.primary.main,
      marginRight: theme.spacing(1),
      borderRadius: 2,
    },
  },
  sectionTitleIcon: { color: theme.palette.primary.main, fontSize: 22, marginRight: theme.spacing(0.5) },
  field: { marginBottom: theme.spacing(2) },
  coverSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
  },
  coverPlaceholder: {
    display: "block",
    width: "100%",
    boxSizing: "border-box",
    border: "2px dashed",
    borderColor: theme.palette.grey[400],
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: theme.palette.grey[50],
    marginTop: theme.spacing(2),
    "&:hover": { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.primary.main + "08" },
  },
  coverPreview: { width: 160, height: 160, objectFit: "cover", borderRadius: "50%", marginTop: theme.spacing(1), display: "block" },
  coverCurrent: { width: 160, height: 160, objectFit: "cover", borderRadius: "50%", marginTop: theme.spacing(1), display: "block" },
  coverActions: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginTop: theme.spacing(1) },
  inputFile: { display: "none" },
  submit: { marginTop: theme.spacing(3) },
  participantsSection: { marginTop: theme.spacing(3) },
  participantsTable: { minWidth: 320 },
  addParticipantRow: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(1), marginBottom: theme.spacing(2) },
}));

const GroupEditForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { groupId } = useParams();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [pictureUrl, setPictureUrl] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [addingParticipant, setAddingParticipant] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [removingSelected, setRemovingSelected] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const coverInputRef = useRef(null);

  const fetchParticipants = React.useCallback(async () => {
    if (!connectionId || !groupId) return;
    setParticipantsLoading(true);
    try {
      const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
        params: { connectionId },
      });
      setParticipants(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setParticipantsLoading(false);
    }
  }, [connectionId, groupId]);

  const fetchGroupInfo = React.useCallback(async (bustCache = false) => {
    if (!connectionId || !groupId) return;
    try {
      const { data } = await api.get(`/group-management/groups/${encodeURIComponent(groupId)}`, {
        params: { connectionId },
      });
      setName(data.subject || "");
      setDescription(data.desc ?? data.description ?? "");
      let url = data.pictureUrl || "";
      if (url && bustCache) url = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
      setPictureUrl(url);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [connectionId, groupId]);

  useEffect(() => {
    if (!connectionId || !groupId) {
      history.replace("/group-management");
      return;
    }
    setLoading(true);
    fetchGroupInfo();
  }, [connectionId, groupId, history, fetchGroupInfo]);

  useEffect(() => {
    if (!loading && connectionId && groupId) fetchParticipants();
  }, [loading, connectionId, groupId, fetchParticipants]);

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    setCoverFile(file || null);
    setCoverPreview(file ? URL.createObjectURL(file) : null);
  };
  const clearCover = () => {
    setCoverFile(null);
    setCoverPreview(null);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!connectionId || !groupId) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("connectionId", connectionId);
      formData.append("name", name.trim());
      formData.append("description", description.trim());
      if (coverFile) formData.append("cover", coverFile);
      await api.patch(`/group-management/groups/${encodeURIComponent(groupId)}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(i18n.t("groupManagement.groupUpdated"));
      clearCover();
      await fetchGroupInfo(!!coverFile);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddParticipant = async () => {
    const num = (newNumber || "").replace(/\D/g, "").trim();
    if (!num) {
      toast.error(i18n.t("groupManagement.participantNumberRequired"));
      return;
    }
    setAddingParticipant(true);
    try {
      await api.post(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
        connectionId,
        participants: [num],
      });
      toast.success(i18n.t("groupManagement.participantAdded"));
      setNewNumber("");
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setAddingParticipant(false);
    }
  };

  const handleRemoveParticipant = async (participant) => {
    setRemovingId(participant.id);
    try {
      await api.delete(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
        data: { connectionId, participants: [participant.id] },
      });
      toast.success(i18n.t("groupManagement.participantRemoved"));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(participant.id);
        return next;
      });
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setRemovingId(null);
    }
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === participants.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(participants.map((p) => p.id)));
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setRemovingSelected(true);
    try {
      await api.delete(`/group-management/groups/${encodeURIComponent(groupId)}/participants`, {
        data: { connectionId, participants: ids },
      });
      toast.success(i18n.t("groupManagement.participantsRemoved", { count: ids.length }));
      setSelectedIds(new Set());
      fetchParticipants();
    } catch (err) {
      toastError(err);
    } finally {
      setRemovingSelected(false);
    }
  };

  if (!connectionId || !groupId) return null;

  if (loading) {
    return (
      <MainContainer>
        <MainHeader>
          <Button className={classes.backButton} onClick={() => history.push("/group-management/groups/edit?" + location.search)} startIcon={<ArrowBackIcon />}>
            {i18n.t("groupManagement.back")}
          </Button>
          <Title>{i18n.t("groupManagement.editGroup")}</Title>
        </MainHeader>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Button
          className={classes.backButton}
          onClick={() => history.push("/group-management/groups/edit?" + (connectionId ? `connectionId=${connectionId}` : ""))}
          startIcon={<ArrowBackIcon />}
        >
          {i18n.t("groupManagement.back")}
        </Button>
        <Title>{i18n.t("groupManagement.editGroup")}</Title>
      </MainHeader>

      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            className={classes.tabs}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label={i18n.t("groupManagement.tabGroupInfo")} icon={<InfoIcon />} />
            <Tab label={i18n.t("groupManagement.participants")} icon={<PeopleIcon />} />
          </Tabs>

          {tabValue === 0 && (
            <div className={classes.tabPanel} role="tabpanel">
              <form onSubmit={handleSubmit}>
                <Typography className={classes.sectionTitle}>
                  {i18n.t("groupManagement.groupName")}
                </Typography>
                <TextField
                  className={classes.field}
                  fullWidth
                  variant="outlined"
                  label={i18n.t("groupManagement.groupName")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={i18n.t("groupManagement.groupNamePlaceholder")}
                />

                <Typography className={classes.sectionTitle}>
                  {i18n.t("groupManagement.groupDescription")}
                </Typography>
                <TextField
                  className={classes.field}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  label={i18n.t("groupManagement.groupDescription")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={i18n.t("groupManagement.groupDescriptionPlaceholder")}
                />

                <Typography className={classes.sectionTitle}>
                  {i18n.t("groupManagement.groupCoverPhoto")}
                </Typography>
                <Box className={classes.coverSection}>
                  {(coverPreview || coverFile) ? (
                    <Box className={classes.coverActions}>
                      {coverPreview && (
                        <img src={coverPreview} alt="" className={classes.coverPreview} />
                      )}
                      <IconButton size="small" onClick={clearCover} title={i18n.t("groupManagement.removeCover")}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  ) : pictureUrl ? (
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {i18n.t("groupManagement.currentCover")}
                      </Typography>
                      <img src={pictureUrl} alt="" className={classes.coverCurrent} />
                    </Box>
                  ) : null}
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className={classes.inputFile}
                    id="edit-group-cover"
                    onChange={handleCoverChange}
                  />
                  <label htmlFor="edit-group-cover" style={{ width: "100%" }}>
                    <Box
                      className={classes.coverPlaceholder}
                      component="span"
                      onClick={() => coverInputRef.current?.click()}
                    >
                      <PhotoCameraIcon style={{ fontSize: 32, color: "gray", marginBottom: 8 }} />
                      <Typography variant="body2" color="textSecondary">
                        {coverFile
                          ? coverFile.name
                          : pictureUrl
                          ? i18n.t("groupManagement.changeCoverPhoto")
                          : i18n.t("groupManagement.addCoverPhoto")}
                      </Typography>
                    </Box>
                  </label>
                </Box>

                <Button
                  type="submit"
                  className={classes.submit}
                  variant="contained"
                  color="primary"
                  disabled={saving}
                >
                  {saving ? "..." : i18n.t("groupManagement.saveGroup")}
                </Button>
              </form>
            </div>
          )}

          {tabValue === 1 && (
            <div className={classes.tabPanel} role="tabpanel">
              <Typography className={classes.sectionTitle}>
                <PeopleIcon className={classes.sectionTitleIcon} />
                {i18n.t("groupManagement.participants")}
              </Typography>
              <Box className={classes.addParticipantRow} flexWrap="wrap">
            <TextField
              size="small"
              variant="outlined"
              placeholder={i18n.t("groupManagement.participantNumber")}
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              style={{ flex: 1, maxWidth: 260 }}
            />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={handleAddParticipant}
              disabled={addingParticipant || !newNumber.trim()}
            >
              {addingParticipant ? "..." : i18n.t("groupManagement.addParticipant")}
            </Button>
            {participants.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleRemoveSelected}
                disabled={removingSelected || selectedIds.size === 0}
              >
                {removingSelected ? "..." : i18n.t("groupManagement.removeSelected")}
                {selectedIds.size > 0 && ` (${selectedIds.size})`}
              </Button>
            )}
          </Box>
          {participantsLoading ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 280 }}>
              <Table className={classes.participantsTable} size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedIds.size > 0 && selectedIds.size < participants.length}
                        checked={participants.length > 0 && selectedIds.size === participants.length}
                        onChange={handleToggleSelectAll}
                        disabled={participants.length === 0}
                      />
                    </TableCell>
                    <TableCell>{i18n.t("groupManagement.participantName")}</TableCell>
                    <TableCell>{i18n.t("groupManagement.participantNumber")}</TableCell>
                    <TableCell align="right">{i18n.t("groupManagement.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="textSecondary">
                          {i18n.t("groupManagement.noParticipants")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    participants.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(p.id)}
                            onChange={() => handleToggleSelect(p.id)}
                          />
                        </TableCell>
                        <TableCell>{p.name || "-"}</TableCell>
                        <TableCell>{p.number || p.id}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveParticipant(p)}
                            disabled={removingId === p.id}
                            title={i18n.t("groupManagement.removeParticipant")}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            </div>
          )}
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default GroupEditForm;
